// D183.3 - the invoice header's order-progress strip: how much of the order is billed by its suppliers.
// Presentational only: every figure is a field of the one `OrderProgress` the page worked out (orderProgress.ts,
// BUSINESS_RULES#client-invoice-live-progress); nothing here adds, rounds or classifies. Internal, never printed.
import React from 'react';
import type { OrderProgress } from './orderProgress';
import type { UnavailableEnteredLine } from '../../lib/clientInvoiceProgressApi';
import { moneyOrBlank } from './profitabilityRows';

interface Props {
  progress: OrderProgress;
  /** Lines with an entered bill that could not be priced (D183.2): named, never dropped silently. */
  unavailable?: ReadonlyArray<UnavailableEnteredLine> | null;
  /** The live read of bills still being entered failed: the figures are then from stored data only. */
  loadFailed?: boolean;
  /** With loadFailed: when the entered figures still shown were last read, already formatted. Absent = never read. */
  staleSince?: string | null;
  currency?: string | null;
}

const REASONS: Record<string, string> = {
  PO_FX_RATE_NOT_PINNED: 'no exchange rate pinned on its PO',
  PO_CURRENCY_NOT_CONVERTIBLE: 'its PO currency cannot be converted',
  ENTERED_COST_PO_LINE_GONE: 'its PO line was removed',
  ENTERED_COST_UNRESOLVED: 'the entered bill could not be priced',
};

export function unavailableTitle(list: ReadonlyArray<UnavailableEnteredLine>): string {
  return list
    .map((u) => {
      const bills = u.invoiceNumbers?.length ? ` (${u.invoiceNumbers.join(', ')})` : '';
      return `Line ${u.lineNumber}: ${REASONS[u.code] || 'could not be read'}${bills}.`;
    })
    .join(' ');
}

export const OrderProgressStrip: React.FC<Props> = ({ progress: p, unavailable, loadFailed, staleSince, currency }) => {
  const m = (v: number | null) => moneyOrBlank(v, currency);
  const failed = unavailable || [];
  const counts =
    `${p.counts.billed} billed · ${p.counts.entered} entered · ${p.counts.waiting} waiting · ${p.counts.closed} closed` +
    (p.counts.hand > 0 ? ` · ${p.counts.hand} costed by hand` : '');
  return (
    <div className="flex flex-wrap items-start gap-x-8 gap-y-1 text-sm" aria-label="Order progress">
      <div>
        <div className="text-xs text-slate-500">Order value</div>
        <div className="font-semibold tabular-nums text-slate-800">{m(p.orderValue)}</div>
      </div>
      <div>
        <div className="text-xs text-slate-500">Billed so far</div>
        <div className="font-semibold tabular-nums text-slate-800">{m(p.billedValue)}</div>
        {p.enteredValue ? <div className="text-xs text-amber-700">{`incl. ${m(p.enteredValue)} entered, not confirmed`}</div> : null}
      </div>
      <div>
        <div className="text-xs text-slate-500">Remaining to collect</div>
        <div className="font-semibold tabular-nums text-slate-800">{m(p.remainingValue)}</div>
      </div>
      <div className="min-w-[10rem]">
        <div className="text-xs text-slate-500">Progress</div>
        <div className="flex items-center gap-2">
          {p.progressPct === null ? (
            <span className="font-semibold text-slate-800">--</span>
          ) : (
            <>
              <div
                role="progressbar"
                aria-label="Order progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={p.progressPct}
                className="h-2 w-24 overflow-hidden rounded bg-slate-200"
              >
                <div className="h-2 bg-emerald-500" style={{ width: `${p.progressPct}%` }} />
              </div>
              <span className="font-semibold tabular-nums text-slate-800">{`${p.progressPct}%`}</span>
            </>
          )}
        </div>
      </div>
      <div className="self-end text-xs text-slate-500">{counts}</div>
      {failed.length > 0 ? (
        <div className="self-end text-xs text-rose-700" title={unavailableTitle(failed)}>
          {failed.length === 1 ? '1 line could not be read live' : `${failed.length} lines could not be read live`}
        </div>
      ) : null}
      {loadFailed ? (
        <div className="self-end text-xs text-rose-700">
          {staleSince
            ? `Bills still being entered could not be refreshed; entered figures are from ${staleSince}`
            : 'Bills still being entered could not be loaded.'}
        </div>
      ) : null}
    </div>
  );
};
